import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  LabelList
} from 'recharts';
import { useAuth } from '../../../../../providers/AuthContext';
import Spinner from '../../../../../components/Spinner';
import { getWidgetById } from '../../../../../services/apiWidgests';
import { useDashboards } from '../../../../../providers/DashboardContext';
import DatabaseOffIco from '../../../../../components/icons/DatabaseOffIco';
import useWidgetRuntime from '../../../../../hooks/useWidgetRuntime';
import { useAppContext } from '../../../../../providers/AppContext';

// Función para formatear segundos -> hh:mm:ss
const formatTime = (seconds) => {
  if (isNaN(seconds)) return '--:--:--';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  return [h, m, s].map(v => String(v).padStart(2, '0')).join(':');
};

// Paleta por nombre (por si usa "palettes")
const colorPalettes = {
  blue: '#3b82f6',
  red: '#ef4444',
  green: '#22c55e',
  yellow: '#eab308',
  purple: '#a855f7',
  gray: '#6b7280'
};

const CustomTooltip = ({ active, payload, isTimeType, textColor }) => {
  if (active && payload?.length) {
    const { value, payload: item } = payload[0];
    const formattedValue = isTimeType
      ? formatTime(value)
      : Number(value) % 1 === 0
      ? Number(value)
      : Number(value).toFixed(2);

    return (
      <div
        className="px-2 py-1 rounded opacity-90"
        style={{ backgroundColor: '#000', color: "white" }}
      >

        <div className="font-semibold">{item.label}</div>
        <div className="text-sm ">{formattedValue}</div>
      </div>
    );
  }
  return null;
};

const CustomLabel = ({ x, y, value, isTimeType, color }) => {
  const formattedValue = isTimeType
    ? formatTime(value)
    : Number(value) % 1 === 0
    ? Number(value)
    : Number(value).toFixed(2);

  const safeY = y < 20 ? y + 20 : y - 10;

  return (
    <text
      x={x}
      y={safeY}
      fill={color || '#374151'}
      fontSize={12}
      textAnchor="middle"
    >
      {formattedValue}
    </text>
  );
};

const TimeRateChartWidget = ({ id }) => {
  const { auth } = useAuth();
  const navigate = useNavigate();
  const { dashboard, selectedDatarange } = useDashboards();
  const { data: runtimeData, loading: fetchingWidgetData } = useWidgetRuntime(id, dashboard?._id, undefined, JSON.stringify(selectedDatarange));

  const [data, setData] = useState([]);
  const [config, setConfig] = useState(null);
  const [fetchingWidgetInfo, setFetchingWidgetInfo] = useState(true);
  const [numericLabel, setNumericLabel] = useState('');
  const [timeLabel, setTimeLabel] = useState('');
  const [isTimeType, setIsTimeType] = useState(false);
  const [textColor, setTextColor] = useState('#374151');
  const [lineColor, setLineColor] = useState('#3b82f6');
  const graphType = config?.graphType || "line";
  const isBars = graphType === "VBars" || graphType === "HBars";
  const isHorizontal = graphType === "HBars";

  const { addToast } = useAppContext();

  const refreshWidgetInfo = async () => {
    try {
      setFetchingWidgetInfo(true);
      const response = await getWidgetById(id, auth?.token);
      const config = response?.configuration || {};
      setConfig(config)
      // Configuración de labels
      setNumericLabel(config?.valueToCompare?.alias || config?.valueToCompare?.key || '');
      setTimeLabel(config?.variableToCompare?.alias || config?.variableToCompare?.key || '');

      // Tipo de dato (si es tiempo)
      setIsTimeType(config?.valueToCompare?.type === 'time');

      // Colores
      setTextColor(config?.textColor || '#374151');

      // Dentro de refreshWidgetInfo()
      const configuredLineColor =
        config?.colorPalette === 'fixed'
          ? config?.fixedColor || '#3b82f6'
          : '#3b82f6'; // fallback si se agregan más paletas después

      setLineColor(configuredLineColor);


      setFetchingWidgetInfo(false);
    } catch (error) {
      if (!error || !error.response) {
        addToast("No se pudo completar la consulta. Inténtalo nuevamente", 2);
        if (typeof setFetchingWidgetInfo !== 'undefined') setFetchingWidgetInfo(false);
        return;
      }
      console.error(error);
      navigate('/logout');
    }
  };

  useEffect(() => {
    refreshWidgetInfo();
  }, [selectedDatarange]);

  // Los datos llegan por socket desde el singleton compartido del backend.
  useEffect(() => {
    setData(runtimeData || []);
  }, [runtimeData]);

  return (
    <div className="w-full h-full p-4 bg-white border border-gray-300 rounded">
      {(fetchingWidgetData || fetchingWidgetInfo) ? (
        <div className="w-full h-full flex justify-center">
          <Spinner />
        </div>
      ) : (
        <>
          <div className="mb-4 flex items-center justify-center">
            <div
              className="w-4 h-4 mr-2 rounded-sm"
              style={{ backgroundColor: lineColor }}
            ></div>
            <span className="text-gray-700">
              {numericLabel} vs {timeLabel}
            </span>
          </div>

          {data.length === 0 ? (
            <div className="w-full h-full flex flex-col items-center justify-center -mt-8">
              <span className="text-lg text-gray-700">No hay datos por el momento</span>
              <DatabaseOffIco width="40px" height="40px" fill="gray" />
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="80%">
              {isBars ? (
                <BarChart
                  layout={isHorizontal ? "vertical" : "horizontal"}
                  data={data}
                  margin={{ top: 30, right: 20, bottom: 20, left: 20 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  {isHorizontal ? (
                    <>
                      <XAxis
                        type="number"
                        stroke="#6b7280"
                        tickFormatter={(v) => (isTimeType ? formatTime(v) : v)}
                      />
                      <YAxis type="category" dataKey="label" stroke="#6b7280" />
                    </>
                  ) : (
                    <>
                      <XAxis dataKey="label" stroke="#6b7280" />
                      <YAxis
                        stroke="#6b7280"
                        tickFormatter={(v) => (isTimeType ? formatTime(v) : v)}
                      />
                    </>
                  )}
                  <Tooltip
                    content={<CustomTooltip isTimeType={isTimeType} textColor={textColor} />}
                  />
                  <Bar dataKey="value" fill={lineColor} isAnimationActive={false}>
                    <LabelList
                      dataKey="value"
                      position={isHorizontal ? "right" : "top"}
                      fill={textColor}
                      formatter={(v) => (isTimeType ? formatTime(v) : (Number(v) % 1 === 0 ? Number(v) : Number(v).toFixed(2)))}
                    />
                  </Bar>
                </BarChart>
              ) : (
                <LineChart data={data} margin={{ top: 30, right: 20, bottom: 20, left: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="label" stroke="#6b7280" />
                  <YAxis
                    stroke="#6b7280"
                    tickFormatter={(v) => (isTimeType ? formatTime(v) : v)}
                  />
                  <Tooltip
                    content={<CustomTooltip isTimeType={isTimeType} textColor={textColor} />}
                  />
                  <Line
                    type="monotone"
                    dataKey="value"
                    stroke={lineColor}
                    strokeWidth={2}
                    dot={{ r: 4, fill: lineColor }}
                    activeDot={{ r: 6, fill: lineColor }}
                    isAnimationActive={false}
                    label={<CustomLabel isTimeType={isTimeType} color={textColor} />}
                  />
                </LineChart>
              )}
            </ResponsiveContainer>
          )}
        </>
      )}
    </div>
  );
};

export default TimeRateChartWidget;
